import { Swiper, SwiperSlide, useSwiper } from "swiper/react";

import { currentDirection } from "@app/lib/helper";
import { cn } from "@app/lib/utils";
import { trans } from "@mongez/localization";
import { useState } from "react";
import "swiper/css";

type SectorServicesSliderProps = {
  services:
    | string[]
    | {
        title: string;
        abbreviation: string;
      }[];
};

export function SectorServicesSlider({ services }: SectorServicesSliderProps) {
  const dir = currentDirection();
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Swiper
      className="w-full transition-none"
      spaceBetween={16}
      slidesPerView={1}
      slidesPerGroup={1}
      onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
      dir={dir}
      key={dir}
      breakpoints={{
        375: {
          slidesPerView: 1,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 2,
          slidesPerGroup: 2,
        },
        1024: {
          slidesPerView: 3,
          slidesPerGroup: 3,
        },
        1280: {
          slidesPerView: 4,
          slidesPerGroup: 4,
        },
      }}
    >
      {services.map((service) => {
        const title =
          typeof service === "string" ? service : service?.title || "";
        const abbreviation =
          typeof service === "string" ? "" : service?.abbreviation || "";

        return (
          <SwiperSlide key={title} className="flex">
            <SectorServiceItem title={title} abbreviation={abbreviation} />
          </SwiperSlide>
        );
      })}
      <ServicesIndicators length={services.length} activeIndex={activeIndex} />
    </Swiper>
  );
}

/**
 * Services Indicators
 */
type ServicesIndicatorsProps = {
  length: number;
  activeIndex: number;
};

export function ServicesIndicators({
  length,
  activeIndex,
}: ServicesIndicatorsProps) {
  const swiper = useSwiper();

  // Calculate the number of indicator dots based on current slidesPerGroup
  const currentSlidesPerGroup = swiper.params.slidesPerGroup as number;
  const indicatorsLength = Math.ceil(length / currentSlidesPerGroup);

  // Calculate which page/group we're currently on
  const currentPage = Math.round(activeIndex / currentSlidesPerGroup);

  return (
    <div className="mt-6 flex w-fit items-center justify-center gap-2 p-2">
      {Array.from({ length: indicatorsLength }).map((_, index) => (
        <button
          key={index}
          onClick={() => swiper.slideTo(index * currentSlidesPerGroup)}
          className={cn(
            "h-2 w-2 rounded-full transition-all duration-300",
            index === currentPage
              ? "w-4 bg-bright-orange"
              : "bg-gray-300 hover:bg-gray-400",
          )}
        />
      ))}
    </div>
  );
}

/**
 * Sector Service Item
 */
type SectorServiceItemProps = {
  title: string;
  abbreviation: string;
};

export function SectorServiceItem({
  title,
  abbreviation,
}: SectorServiceItemProps) {
  return (
    <div className="relative flex min-h-36 w-full flex-col gap-4 rounded-3xl border border-[#F3F3F3] bg-white p-4 pt-10 text-base font-semibold text-marine sm:p-6 sm:text-lg lg:text-xl">
      <span className="h-1 w-14 rounded-full bg-bright-orange" />
      {trans(title)}{" "}
      {abbreviation && (
        <span className="text-sm font-normal text-marine">
          ({abbreviation})
        </span>
      )}
    </div>
  );
}
